import React from 'react';
import { FaLightbulb, FaChartLine, FaMoneyBillWave } from 'react-icons/fa';
import Layout from '../components/Layout';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import Image from 'next/image';
import Carousel from '../components/Carousel';
import { Roboto_Condensed } from 'next/font/google';
import ChakraMarkdown from '../components/ChakraMarkdown';
import DownloadApp from '../components/DownloadApp';
import Seo from '../components/Seo';

// Subsets are really important.
const roboto = Roboto_Condensed({
  weight: '400', // if single weight, otherwise you use array like [400, 500, 700],
  style: 'normal', // if single style, otherwise you use array like ['normal', 'italic']
  subsets: ['latin'],
});

const OPTIONS = [
  {
    icon: <FaMoneyBillWave size={'30px'} color="#11a00d" />,
    title: 'Maximise your earnings',
    description: `As a Vert Chauffeur, you are linked with discerning clientele. Whether it's pre-booked or on-demand, 
    Vert provides a variety of services, including Government Appointments, Airport Transfers, Road 
    Shows, and VIP Private Hire experiences.`,
  },
  {
    icon: <FaChartLine size={'30px'} color="#11a00d" />,
    title: 'Special offers & Promotions',
    description: `We are partnering with services and products suppliers so you
    can have acccess to exclusive offers on the market. So we can
    reward your hard work, we would like to surprise you from time
    to time with special promotions or gifts.`,
  },
  {
    icon: <FaLightbulb size={'30px'} color="#11a00d" />,
    title: 'New skills & Qualifications',
    description: `At Vert, our unwavering commitment lies in investing in our Chauffeurs. Whether it's acquiring new 
    skills, obtaining fresh qualifications, or prioritizing the health and well-being of our Chauffeurs, we 
    have established partnerships with industry leaders to offer the most cutting-edge training programs 
    available. Safety. Training. Health. Education. Skills.`,
  },
];

const driverQuestions = [
  {
    question: 'What do I need to create an account?',
    answer:
      "To create a Vert account, please follow these steps:\n1.Download the Vert Chauffeur App. \n2.Enter your mobile number to receive a verification code. Once validated, you'll be prompted to provide your personal information and car registration details. You'll also need to set a password and agree to our terms and conditions and privacy notice. \n3.Fill in your First Name, Last Name, Gender, and Private Hire Driver License Number. \n4.Upload a clear picture of your ID and a professional photo for your Vert Chauffeur App profile. This profile picture will be visible to clients when you accept jobs through Vert. \n5.Vert will verify all the documents you upload. They will have a status of Pending, Approved, or Rejected. If any document is rejected, you will need to resubmit that specific document. \n6.Once your car documents are verified, your car will be assigned to your account. \n7.After all necessary documentation is checked and approved by Vert, you will receive an email \nconfirming your account registration. You'll then have access to your account in the Vert Chauffeur App. \n8.To start accepting jobs as a Vert Chauffeur, you'll need to enter your payment information, including Sort Code, Account Number, and long card number, so we can process payouts to your account. ",
  },
  {
    question: 'Where can I see my past, upcoming, and new trips?',
    answer:
      '1. Tap into the main menu of the app\n2. Select “Your Trips”\n3. Select either the “Past”, “Scheduled” or “New” trip list',
  },
  {
    question: 'Can I cancel a ride?',
    answer:
      'We understand that sometimes things do not work as planned and that you may need to cancel a ride. You can cancel from your app, however, depending on the situation a cancellation fee may apply. If you want to know more about our cancellation policy, please check the Driver Terms and Conditions in your App under the tab Legal/Terms and Conditions. ',
  },
  {
    question: 'When can I report a rider?',
    answer: `Your safety is of utmost importance to us. Therefore, we've streamlined the process for you to share your ride experience and report any concerns about a fellow rider if you ever feel unsafe during a trip. Here's how you can do it: \n\n1. At the end of your ride, utilize the "Safety" rating scale.\n2. Choose the "Report Rider" option.\n3. Complete the report user form that you'll be directed to, providing as many details as possible regarding the circumstances that led to your report.
      \nIt's crucial to understand that this is a serious procedure and should only be used in situations where your safety has genuinely been compromised. In such cases, both your account and the rider's account will be temporarily suspended while further verifications are conducted. Additionally, we will promptly report any dangerous or inappropriate behaviour to the appropriate authorities. Your safety remains our top priority. 
      `,
  },
  {
    question: 'What occurs when a rider reports you?',
    answer: `If a rider reports you, we will carefully examine the concerns outlined in the rider's report form. It's essential to emphasize that this is a highly significant procedure, reserved for situations where there has been a genuine compromise of safety. In such instances:\n1. Your account, as well as the rider's account, will be temporarily suspended while additional verifications are conducted.\n2. Any incidents involving dangerous or inappropriate behaviour will be promptly reported to the police.\n\nWe take reports seriously to ensure the safety and security of all our users.
    `,
  },
  {
    question: 'What is the Emergency/Panic button?',
    answer:
      'At Vert, we are dedicated to collaborating with our users and stakeholders to elevate the safety of everyone involved in rides facilitated by our platform. ',
  },
];

const Driver = () => {
  const [isLargerThan728] = useMediaQuery('(min-width: 728px)');
  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');
  const [isLargerThan1350] = useMediaQuery('(min-width: 1350px)');

  let seo = {};

  return (
    <Layout withAppNavBar={false}>
      <Seo seo={seo} />
      <Flex as="section" direction={'column'} justify="center">
        <Flex
          direction="row"
          justifyContent="center"
          alignItems="center"
          w="100%"
          h="100vh"
          position="relative"
        >
          <Flex
            direction="column"
            px={isLargerThan1024 ? '200px' : isLargerThan728 ? '100px' : '30px'}
            py="60px"
            w="100%"
            h="100%"
            backgroundColor="blackAlpha.600"
            paddingTop={200}
          >
            <Image
              src="/images/chauffeur_background.webp"
              alt="Chauffeur with Vert - Become a Chauffeur"
              layout="fill"
              objectFit="cover"
              style={{ zIndex: -1 }}
            />
            <Flex
              direction="column"
              alignSelf="center"
              w={isLargerThan1024 ? '1100px' : '100%'}
            >
              <Text
                as="h1"
                className={roboto.className}
                color="white"
                fontWeight="bold"
                fontSize="5xl"
                py="20px"
              >
                Chauffeur with Vert!
              </Text>
              <Box py={3}>
                <Text color="white" fontSize="lg" mb={0}>
                  Sign up today and become a Vert Chauffeur{' '}
                  {isLargerThan728 && <br />}
                  with great benefits and more!
                </Text>
              </Box>
            </Flex>
          </Flex>
        </Flex>

        {/* Features Section */}
        <Flex
          as="section"
          my={20}
          direction="column"
          justifyContent="center"
          alignItems="center"
          px="20px"
          maxW="1524px"
          alignSelf={isLargerThan728 ? 'center' : 'auto'}
        >
          <Box
            w={
              isLargerThan1350 ? '1460px' : isLargerThan728 ? '1024px' : '100%'
            }
          >
            <Carousel paginationAndArrows={false} itemsToShowInitial={4}>
              {OPTIONS.map((item, index) => (
                <Flex
                  as="article"
                  direction="column"
                  mx={5}
                  my={10}
                  shadow="lg"
                  key={index}
                  h={isLargerThan728 ? '400px' : '450px'}
                  alignItems="center"
                  rounded="md"
                  borderWidth={0.5}
                  overflow="hidden"
                  w={600}
                >
                  <Flex
                    w={400}
                    height={200}
                    overflow="hidden"
                    alignItems="center"
                    justify={'center'}
                  >
                    {item.icon}
                  </Flex>
                  <Box p={5}>
                    <Text as="h3" className={roboto.className} fontSize="2xl">
                      {item.title}
                    </Text>
                    <Text fontSize={12}>{item.description}</Text>
                  </Box>
                </Flex>
              ))}
            </Carousel>
          </Box>
        </Flex>

        {/* FAQ Section */}
        <Flex
          as="section"
          mt={0}
          mb={20}
          direction="column"
          justifyContent="center"
          alignItems="center"
          px={isLargerThan1024 ? '200px' : 0}
        >
          <Text
            as="h2"
            className={roboto.className}
            py={10}
            textAlign="center"
            fontSize="5xl"
          >
            Frequently Asked Questions
          </Text>
          <Box w="100%">
            <Accordion allowToggle>
              {driverQuestions.map((item, index) => (
                <AccordionItem key={index}>
                  <AccordionButton>
                    <Box
                      as="span"
                      flex="1"
                      textAlign="left"
                      fontSize="0.9rem"
                      fontWeight="600"
                    >
                      {item.question}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel pb={4}>
                    <ChakraMarkdown
                      content={item.answer}
                      onLinkClick={() => null}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </Flex>
      </Flex>

      <DownloadApp type="driver" />
    </Layout>
  );
};

export default Driver;
